import { useSnackbar } from 'notistack';
import { useTranslation } from 'next-i18next';

import { updateProposal } from '../../../../slices/proposalsSlice';

import { MessageType } from '../../../../components/UI/snackbar/providers/SnackBarProvider';
import { useAppDispatch } from '../../../../app/hooks';
import { useAppStore } from '../../../../app/hooks/useAppStore';
import DollarSymbol from '../../../../../public/images/icons/dollar-symbol.svg';
import useGetRelatedContactId from '../useGetRelatedContactId';
import ProposalEntity, { PriceProposalStatus } from 'src/db/entities/proposal/ProposalEntity';
import { selectProposalsById } from 'slices/proposalsSlice';

export default function useUpdateProposal() {
  const { t } = useTranslation('proposal-form');

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const getRelatedContactId = useGetRelatedContactId();

  const update = async (
    proposal: ProposalEntity,
    onSuccess?: (data: ProposalEntity) => void | Promise<void>
  ) => {
    try {
      const relatedContactId = await getRelatedContactId(proposal.contactId);

      const proposalsById = selectProposalsById(store.getState());
      const original = proposalsById[proposal.uuid];

      const wasSent = original?.status === PriceProposalStatus.SENT;

      const comparable = (p: ProposalEntity) => ({
        title: p.title,
        client: p.client,
        items: p.items,
        description: p.description,
        discountAmount: p.discountAmount,
        discountType: p.discountType,
        contactId: p.contactId,
        status: p.status,
        settings: {
          currency: p.settings?.currency,
          includesTax: p.settings?.includesTax,
        },
      });

      const baseData: ProposalEntity = {
        ...proposal,
        contactId: relatedContactId,
        updatedAt: Date.now(),
      };

      const hasChanged = original
        ? JSON.stringify(comparable(baseData)) !== JSON.stringify(comparable(original))
        : true;

      const data: ProposalEntity = {
        ...baseData,
        ...(wasSent && hasChanged
          ? { status: PriceProposalStatus.DRAFT, isDraft: true }
          : {}),
      };

      await dispatch(updateProposal(data)).unwrap();

      enqueueSnackbar({
        message: t('form.notification.updated'),
        variant: MessageType.Success,
        icon: <DollarSymbol />,
      });

      if (wasSent && hasChanged) {
        enqueueSnackbar({
          message: t('form.notification.statusChangedToDraft'),
          variant: MessageType.Info,
        });
      }

      if (onSuccess) {
        await onSuccess(data);
      }
    } catch (error) {
      const status = error.response?.status;
      const message = !status
        ? t('common:message.network_error')
        : status === 409
        ? t('form.notification.signedReadOnly')
        : t('common:message.server_error');

      enqueueSnackbar({
        message,
        variant: MessageType.Error,
      });
    }
  };

  return update;
}
